"use client"

import Navbar from "@/components/landing/navbar";
import Footer from "@/components/landing/footer";
import { defaultCountryConfig } from "@/config/counties/default";
import { LEGAL_UPDATED } from "@/lib/content-dates";

// One source for the date shown here and the `lastmod` the sitemap sends
// Google (lib/content-dates.ts). They used to disagree by over a year: this
// page read "January 5, 2025" — literal brackets and all, and predating the
// domain's own 2026 registration — while the sitemap reported 2026-05-01.
const LAST_UPDATED = LEGAL_UPDATED.toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

export default function TermsPageContent() {
  return (
    <div className="min-h-screen bg-card">
      <Navbar data={defaultCountryConfig.navbar} />
      <div className="pt-16">
        <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 max-w-4xl">
          {/* Header Section */}
          <div className="mb-12 pb-8 border-b-2 border-primary/20">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4 tracking-tight">
              Terms of Use
            </h1>
            <div className="h-1 w-24 bg-primary rounded-full mb-6"></div>
            <div className="text-sm text-muted-ink space-y-1">
              <p>
                <strong className="text-body">Last Updated:</strong> January 5, 2025
              </p>
              <p>
                <strong className="text-body">Effective Date:</strong> {LAST_UPDATED}
              </p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-body space-y-8 leading-relaxed">
            <p className="leading-relaxed">
              This Terms of Use ("Terms" or this "Agreement") is a contract between you ("you," "User," or "Subscriber") and LeadsMiner ("LeadsMiner," "we," "us," or "our") governing your use of our website located at <a href="https://www.leadsminer.app/" target="_blank" rel="noopener noreferrer">https://www.leadsminer.app/</a> and all associated products, features, content, and services (collectively, the "Services").
            </p>

            <p className="leading-relaxed">
              By accessing or using the Services, you agree to be bound by these Terms, including LeadsMiner's Privacy Policy incorporated herein by reference. If you do not agree to all of these Terms, you are prohibited from accessing or using the Services.
            </p>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">1.</span> Acceptance of Terms; Arbitration Notice
              </h2>
              <p className="leading-relaxed">
                By registering for the Services, creating an account, or using the Services in any way, you represent that you have read, understand, and agree to these Terms.
              </p>
              <p><strong className="text-ink">Arbitration Notice:</strong></p>
              <p className="leading-relaxed">
                You and LeadsMiner agree that any dispute arising from these Terms will be resolved through binding, individual arbitration, and you waive the right to a jury trial or class action except as otherwise expressly provided. (See the "Arbitration" section below.)
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">2.</span> Eligibility
              </h2>
              <p className="leading-relaxed">
                The Services are available only to individuals who are at least 18 years old. By using the Services, you warrant that you are at least 18 years of age. If you are not, you must immediately stop using the Services.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">3.</span> Accounts and Security
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">a) Creating an Account</h3>
              <p className="leading-relaxed">To use parts of the Services, you must create an account. You agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, complete information including name, email, and business information.</li>
                <li>Maintain and promptly update your account information.</li>
                <li>Be responsible for all activity under your account.</li>
              </ul>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">b) Account Security</h3>
              <p className="leading-relaxed">You are responsible for safeguarding your account credentials and any activity occurring under your account.</p>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">c) Human Users Only</h3>
              <p className="leading-relaxed">Accounts created by bots or automated means are prohibited.</p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">4.</span> Use of Services and Leads
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">a) Permitted Uses</h3>
              <p className="leading-relaxed">Subject to your compliance with these Terms, LeadsMiner grants you a limited, non‑exclusive, non‑transferable license to access and use the Services and the business leads you purchase for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Identifying prospective business opportunities,</li>
                <li>Researching existing prospects and customers,</li>
                <li>Communication and outreach in connection with legitimate professional purposes consistent with applicable laws.</li>
              </ul>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">b) Prohibited Uses</h3>
              <p className="leading-relaxed">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Services or leads for unlawful or unauthorized purposes, including violating data protection, spam, or privacy laws.</li>
                <li>Reproduce, resell, redistribute, or sublicense lead data or any content derived from the Services.</li>
                <li>Engage in scraping, mining, automated extraction, or other automated access of data.</li>
                <li>Alter, remove, or circumvent security or proprietary notices.</li>
                <li>Use leads in connection with malware, spyware, or harmful content or software.</li>
              </ul>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">c) Accuracy</h3>
              <p className="leading-relaxed">You agree to verify the accuracy, completeness, and fitness of lead data before relying on it. LeadsMiner does not guarantee accuracy of lead information.</p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">5.</span> Subscription, Payments, and Renewals
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">a) Subscriptions</h3>
              <p className="leading-relaxed">LeadsMiner may offer free and paid subscription plans. Subscribers pre‑pay for access to a defined set of leads or credits.</p>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">b) Billing</h3>
              <p className="leading-relaxed">Payment methods must be valid and kept current. Fees are non‑refundable except as required by applicable law or at LeadsMiner's discretion.</p>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">c) Expiration</h3>
              <p className="leading-relaxed">Purchased credits or lead access expire in accordance with your subscription terms.</p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">6.</span> Intellectual Property
              </h2>
              <p className="leading-relaxed">
                All intellectual property rights in the Services, including software, content, trademarks, and data structure, are owned by LeadsMiner or licensors. This Agreement does not grant you any ownership rights.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">7.</span> Disclaimer of Warranties
              </h2>
              <p className="leading-relaxed">
                The Services and all lead data are provided "AS IS" and "AS AVAILABLE," without warranty of any kind. LeadsMiner expressly disclaims any warranty that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The Services will meet your needs or expectations,</li>
                <li>Lead data is accurate, complete, or up‑to‑date,</li>
                <li>The Services will be uninterrupted, secure, or error‑free.</li>
              </ul>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">8.</span> Limitation of Liability
              </h2>
              <p className="leading-relaxed">To the full extent permitted by applicable law, LeadsMiner and its affiliates will not be liable for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Any indirect, incidental, special, consequential, or punitive damages,</li>
                <li>Loss of profits, data, or business opportunities,</li>
                <li>Any amount exceeding what you paid LeadsMiner in the three (3) months before the claim arose.</li>
              </ul>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">9.</span> Indemnification
              </h2>
              <p className="leading-relaxed">You agree to defend, indemnify, and hold harmless LeadsMiner, its officers, directors, employees, agents, and affiliates from any claims arising from:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the Services,</li>
                <li>Your breach of these Terms,</li>
                <li>Your violation of applicable laws or third‑party rights.</li>
              </ul>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">10.</span> Arbitration; Waiver of Jury Trial
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">a) Agreement to Arbitrate</h3>
              <p className="leading-relaxed">
                Except for matters that may be decided by small claims court, disputes under these Terms will be resolved by final and binding arbitration under the rules of the American Arbitration Association (AAA) or similar neutral arbitration provider.
              </p>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">b) Waiver of Class Actions</h3>
              <p className="leading-relaxed">
                You and LeadsMiner agree that disputes will be arbitrated only on an individual basis. Class arbitrations, class actions, and representative actions are prohibited.
              </p>
              <h3 className="text-lg sm:text-xl font-semibold text-ink mt-6 mb-3 text-primary/90">c) Opt‑Out</h3>
              <p className="leading-relaxed">
                If you do not wish to be bound by arbitration, you may opt out within 30 days of agreeing to these Terms by sending written notice to LeadsMiner at <a href="mailto:support@leadsminer.app" className="font-mono text-primary hover:underline">support@leadsminer.app</a> with your name, email, and statement that you are opting out of the arbitration agreement.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">11.</span> Changes to Terms
              </h2>
              <p className="leading-relaxed">
                LeadsMiner may update these Terms at any time. Material changes will be communicated via email or through the Services. Continued use of the Services after changes constitutes acceptance.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">12.</span> Governing Law
              </h2>
              <p className="leading-relaxed">
                These Terms are governed by the laws of the State of California, USA without regard to conflicts of law principles, and the state or federal courts in that location will have exclusive jurisdiction for any matter not subject to arbitration under Section 10.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">13.</span> Electronic Communications
              </h2>
              <p className="leading-relaxed">
                By using the Services, you agree to receive electronic communications from LeadsMiner, including notices, agreements, disclosures, and other information required by law.
              </p>
            </section>

            <hr className="my-10 border-line" />

            <section className="space-y-4 pt-6">
              <h2 className="text-xl sm:text-2xl font-bold text-ink mb-4 pb-2 border-b border-line">
                <span className="text-primary">14.</span> Contact Information
              </h2>
              <p className="leading-relaxed">
                If you have questions about these Terms, email us at:{" "}
                <a href="mailto:support@leadsminer.app" className="font-mono text-primary hover:underline">
                  support@leadsminer.app
                </a>
              </p>
              <div className="mt-4 p-4 bg-surface-alt rounded-lg border border-line">
                <p className="font-semibold text-ink mb-1">LeadsMiner</p>
                <p className="text-muted-ink">
                  <a href="mailto:support@leadsminer.app" className="font-mono text-primary hover:underline">
                    support@leadsminer.app
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer data={defaultCountryConfig.footer} />
    </div>
  );
}
